import './apple-pay-button';
import cardLogoStyles from '../../../common/styles/card-logo-styles';
import emit from '../../../common/events';
import paymentStyles from '../../../common/styles/payment-styles';
import store from '../../../store/store';
import { ActionStatus } from '../../../common/constants/action-status';
import { ClientTokenDetails, PaymentDetails, PaymentType, RmsBookstoreAddress } from '../../checkout/shop-checkout.interface';
import { ERROR_ADDRESS_VALIDATION_MESSAGE } from '../utils/braintree.util';
import { MessageType } from '../../alert/global-message.interface';
import { PaymentMethod, PaymentMethodTemplate } from '../payment-method';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getAddressValidationError, getAddressValidationStatus } from '../../address/store/address-validation';
import { getValidatedAddress } from '../../address/utils/address.util';
import {
  getActiveStepId,
  getApplePayBillingAddress,
  getClientTokenResponse,
  getPaymentDetails,
  resetPaymentDetails,
  updateApplePayBillingAddress,
  updateBillingAddress,
  updatePaymentDetails
} from '../../checkout/shop-checkout';
import { handleApiError } from '../../../common/utils/api-utils';
import { html, nothing } from 'lit';
import {
  isValidApplePayDetail,
  render3rdPartyPaymentCardInfo
} from '../../../common/utils/payment-util';

/**
 * Apple Pay payment method that loads the Apple Pay button.
 * Renders Apple Pay payment results.
 */
@customElement(`mhecomm-apple-pay-payment-method`)
export class ApplePayPaymentMethod extends connect(store)(PaymentMethod) implements PaymentMethodTemplate {
  static styles = [paymentStyles, cardLogoStyles];

  /**
   * Country code for Apple Pay transaction.
   */
  @property()
  countryCode: string = 'US';

  /**
   * Currency for Apple Pay transaction.
   */
  @property()
  currency: string = 'USD';

  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @property({converter: booleanConverter})
  shippable: boolean = false;

  /**
   * Apple Pay payment details.
   */
  @state()
  private applePaymentDetail: PaymentDetails | undefined;

  /**
   * Braintree Client token to initialize Apple Pay button.
   */
  @state()
  private clientToken: string = '';

  /**
   * Controls the visibility of the error message when the address validation has failed.
   */
  @state()
  private errorAddressValidation: boolean = false;

  /**
   * Opens the Apple Pay modal if the user has clicked on change button.
   */
  private autoOpen = false;

  stateChanged(state: StoreState): void {
    this.updateIsSelected(PaymentType.BRAINTREE_APPLE_PAY, state);

    if (getAddressValidationStatus(state) === ActionStatus.failed) {
      handleApiError(getAddressValidationError(state));
      return;
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mhecomm-apple-pay-checkout-info-collected', this.onCollectApplePayInfo);
    this.addEventListener('mhecomm-apple-pay-button-ready-to-payment', this.isReadyToPay);

    const state = store.getState() as StoreState;
    const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(state);
    this.clientToken = clientTokenResponse!.token;
    this.billingAddress = getApplePayBillingAddress(state)!;
    if (this.billingAddress) {
      store.dispatch(updateBillingAddress(this.billingAddress));
    }

    const paymentDetail = getPaymentDetails(state);
    if (paymentDetail) {
      if (isValidApplePayDetail(paymentDetail)) {
        this.applePaymentDetail = paymentDetail;
      } else {
        store.dispatch(resetPaymentDetails());
      }
    }
  }

  renderComp() {
    if (!this.isSelected) {
      return nothing;
    }

    if (this.applePaymentDetail || this.errorAddressValidation) {
      return this.renderApplePayPaymentData(this.applePaymentDetail);
    } else {
      return this.renderApplePayButton();
    }
  }

  /**
   * Renders the Apple Pay button.
   */
  renderApplePayButton() {
    return html`
      <div class="d-flex justify-content-start">
        <div id="apple-payment-method-button" data-automation-id="apple-payment-method-button">
          <mhecomm-apple-pay-button
            amount="${this.totalAmount}"
            buttonColor="black"
            buttonSize="small"
            buttonType="buy"
            clientToken="${this.clientToken}"
            currencyCode=${this.currency}
            countryCode="${this.countryCode}"
            ?shippable=${this.shippable}
          ></mhecomm-apple-pay-button>
        </div>
      </div>
    `;
  }

  /**
   * Renders Apple Pay payment details saved on the checkout.
   *
   * @param paymentDetails
   */
  renderApplePayPaymentData(paymentDetails?: PaymentDetails) {
    return html`
      ${this.errorAddressValidation ? html`
        <div class="mt-3" id="apple-pay-invalid-address" data-automation-id="apple-pay-invalid-address">
          <mhecomm-alert type=${MessageType.danger} message=${ERROR_ADDRESS_VALIDATION_MESSAGE}></mhecomm-alert>
        </div>
      ` : nothing}
      <div id="payment-method-details-container" class="d-flex flex-row flex-wrap border rounded p-3">
        ${render3rdPartyPaymentCardInfo(this.billingAddress, paymentDetails)}
        <div class="d-inline col-12 col-sm-4 text-sm-end">
          <button
            class="btn btn-link p-0"
            @click=${this.removePaymentDetails}
            data-automation-id="apple-pay-payment-details-remove"
            aria-label="Remove Apple Pay payment method"
          >Remove</button>
        </div>
      </div>
    `;
  }

  /**
   * Collects Apple Pay billing address and validates it.
   * If it is valid, the Redux state is updated with the payment and billing address.
   * Otherwise, only the local state is updated to display the invalid data.
   *
   * @param e
   */
  public onCollectApplePayInfo(e: Event): void {
    const event = e as CustomEvent;
    const applePayBillingAddress: RmsBookstoreAddress = event.detail.billingAddress;

    getValidatedAddress(applePayBillingAddress)
      .then((validatedAddress): void => {
        store.dispatch(updateApplePayBillingAddress(validatedAddress));
        store.dispatch(updateBillingAddress(validatedAddress));
        this.updateApplePaymentDetails(event.detail.paymentDetails);
        this.errorAddressValidation = false;
      })
      .catch((error): void => {
        this.billingAddress = applePayBillingAddress;
        this.errorAddressValidation = true;
        this.applePaymentDetail = event.detail.paymentDetails;
        console.error('failed to validate address', error);
      });
  }

  /**
   * Updates the payment details with the data collected from Apple Pay.
   *
   * @param paymentDetails
   */
  updateApplePaymentDetails(paymentDetails: PaymentDetails): void {
    store.dispatch(updatePaymentDetails(paymentDetails));
    const activeStepId = getActiveStepId(store.getState() as StoreState);
    emit({
      element: this,
      name: `${activeStepId}-checkout-step-complete`,
      detail: {id: activeStepId},
    });
  }

  /**
   * Removes the Apple Pay payment details from the checkout.
   */
  removePaymentDetails(): void {
    this.billingAddress = this.billingAddressInitialState;
    this.errorAddressValidation = false;
    this.applePaymentDetail = undefined;
    store.dispatch(resetPaymentDetails("Apple Pay"));
  }

  /**
   * Clicks on the Apple Pay button if autoOpen is set to true.
   */
  private isReadyToPay(): void {
    if (this.autoOpen) {
      const appleButton = this.shadowRoot!.querySelector('mhecomm-apple-pay-button');
      appleButton!.shadowRoot!.querySelector('button')?.click();
      this.autoOpen = false;
    }
  }

  /**
   * Validates the Apple Pay payment method.
   */
  public async validatePaymentMethod(): Promise<void> {
    const paymentDetails = getPaymentDetails(store.getState() as StoreState)!;
    if (!paymentDetails.paymentNonce) {
      throw new Error('Apple Pay payment failed.');
    }
  }

  /**
   * Edits the Apple Pay payment method.
   */
  paymentMethodEditAction(): void {
    return;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-apple-pay-payment-method': ApplePayPaymentMethod;
  }
}
